import { React, useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import axios from "../../api/axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

const LOGIN_URL = "/api/account/signin";

export default function SignIn() {
  const { setAuth, persist, setPersist } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  //const from = location.state?.from?.pathname || "/admin/dashboard";

  const from = location.state?.from?.pathname;

  const userRef = useRef();
  const pwdRef = useRef();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [emailErrMsg, setEmailErrMsg] = useState("");
  const [pwdErrMsg, setPwdErrMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  // ✅ Add loader state
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");

    if (reason === "expired") {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Sesioni juaj ka skaduar",
        text: "Ju lutem identifikohuni përsëri.",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
        background: "#ffffff",
        color: "#0d9488",
        iconColor: "#0d9488",
        customClass: {
          popup: "shadow-lg rounded-xl border border-gray-100",
        },
      });

      window.history.replaceState({}, document.title, "/auth/sign-in");
    }
  }, []);

  useEffect(() => {
    setEmailErrMsg("");
    setPwdErrMsg("");
    setErrMsg("");
  }, [email, pwd]);

const handleSubmit = async (e) => {
  e.preventDefault();

  if (loading) return;
  setLoading(true);

  if (email.trim() === "") {
    setEmailErrMsg("Email adresa duhet të plotësohet");
    setLoading(false);
    return;
  }

  if (pwd.trim() === "") {
    setPwdErrMsg("Fjalëkalimi duhet të plotësohet");
    setLoading(false);
    return;
  }

  try {
    const body = { email, password: pwd };
    const response = await axios.post(LOGIN_URL, JSON.stringify(body), {
      headers: { "Content-Type": "application/json" },
    });

    const accessToken = response?.data?.Token;
    const role = response?.data?.Roles; // string: "Admin" or "Admin_Police"

    localStorage.setItem("token", accessToken);
    localStorage.setItem("roles", role);

    // optional: store user info
    localStorage.setItem("userId", response?.data?.Id);
    localStorage.setItem("name", response?.data?.Name);

    setAuth({ email, pwd, roles: role, accessToken });

    // Artificial delay for loader
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // Role-based redirect
    let redirectPath = "/admin/dashboard"; // default admin
    if (role === "admin_police" || role === "assistant_police") redirectPath = "/admin/dashboard-police";

    setEmail("");
    setPwd("");
    navigate(redirectPath, { replace: true });

  } catch (err) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (err.response?.status === 400) setErrMsg("Kredencialet e gabuara");
    else if (err.response?.status === 401) setErrMsg("Nuk jeni të autorizuar për qasje!");
    else setErrMsg("Gabim teknik");
  } finally {
    setLoading(false);
  }
};

  const togglePersist = () => {
    setPersist((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem("persist", persist);
  }, [persist]);

  return (
    <div className="mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[16vh] w-full max-w-full flex-col items-center lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700">Kyçu</h4>
        <p className="mb-8 ml-1 text-base text-gray-600">
          Ju lutemi plotësoni email adresen dhe fjalëkalimin për tu kyçur!
        </p>

        {/* Inputs */}
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="text"
          ref={userRef}
          autoComplete="off"
          className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none"
          value={email}
          required
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailErrMsg("");
          }}
        />
        {emailErrMsg && <p className="mb-2 text-sm text-red-500">{emailErrMsg}</p>}

        <div className="pt-4">
          <label htmlFor="password" className="mt-2">
            Fjalëkalimi
          </label>
          <input
            id="password"
            type="password"
            className="flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none"
            value={pwd}
            required
            ref={pwdRef}
            onChange={(event) => {
              setPwd(event.target.value);
              setPwdErrMsg("");
            }}
          />
          {pwdErrMsg && <p className="mb-2 text-sm text-red-500">{pwdErrMsg}</p>}
        </div>

        {/* Error Message */}
        {errMsg && <p className="mb-2 text-sm text-red-500">{errMsg}</p>}

        {/* ✅ Login Button with Loader */}
        <button
          className={`linear mt-4 w-full rounded-xl py-[12px] text-base font-medium text-white transition-all duration-200 ${
            loading ? "bg-brand-600 cursor-not-allowed opacity-80" : "bg-brand-500 hover:bg-brand-600"
          }`}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <div className="flex justify-center items-center gap-2">
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Duke u kyçur...
            </div>
          ) : (
            "Kyçu"
          )}
        </button>
      </div>
    </div>
  );
}
